import { alpha } from "@mui/material"
import config from "config"

/**
 * Instruction from mui doc
 * ==============================
 * Ref: https://mui.com/material-ui/customization/theming/
 * Theme composition: using theme options to define other options
 * When the value for a theme option is dependent on another theme option, you should compose the theme in steps.
 */
/**
 * Override basic value: palette, typography, shadow, borderRadius
 */

const mixins = {
  bottomNavigation: {
    height: 75,
  },
  mobileHeader: {
    height: 90,
  },
  mobileDirectionSearch: {
    width: 380,
  },
}

export const themeOptionFactory = (theme) => ({
  mixins,
  components: {
    MuiButton: {
      styleOverrides: {
        outlinedSizeSmall: {
          fontSize: "0.75rem",
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          padding: 0,
          width: "fit-content",
          height: "20px",
        },
        switchBase: {
          padding: 0,
          margin: theme.spacing(1.4),
          marginTop: theme.spacing(0.2),
          transitionDuration: theme.transitions.duration.standard,
          "&.MuiButtonBase-root:hover": {
            backgroundColor: "transparent",
          },
          "&.Mui-checked": {
            transform: "translateX(7px)",
            color: theme.palette.primary.contrastText,
            "& + .MuiSwitch-track": {
              backgroundColor: theme.palette.primary.main,
              opacity: 1,
            },
            "&.Mui-disabled + .MuiSwitch-track": {
              opacity: 0.5,
            },
          },
          "&.Mui-focusVisible .MuiSwitch-thumb": {
            color: theme.palette.primary.main,
            border: `6px solid ${theme.palette.primary.contrastText}`,
          },
          "&.Mui-disabled .MuiSwitch-thumb": {
            color: theme.palette.grey[100],
          },
          "&.Mui-disabled + .MuiSwitch-track": {
            opacity: 0.7,
          },
        },
        thumb: {
          boxSizing: "border-box",
          width: "16px",
          height: "16px",
          marginLeft: theme.spacing(-1),
        },
        track: {
          width: "30px",
          height: "20px",
          borderRadius: "10px",
          backgroundColor: theme.palette.grey[300],
          opacity: 1,
          transition: theme.transitions.create(["background-color"], {
            duration: 500,
          }),
        },
      },
    },
    MuiCard: {
      defaultProps: {
        elevation: 15,
      },
      styleOverrides: {
        root: {
          borderRadius: "0",
          color: "#000000",
        },
      },
    },
    MuiBottomNavigation: {
      styleOverrides: {
        root: {
          width: "100%",
          maxWidth: 400,
          borderTopLeftRadius: 10,
          borderTopRightRadius: 10,
          backgroundColor: theme.palette.background.paper,
          boxShadow: theme.shadows[23],
          height: mixins.bottomNavigation?.height || 75,
        },
      },
    },
    MuiBottomNavigationAction: {
      styleOverrides: {
        root: {
          padding: "16px 12px",
          "& .MuiSvgIcon-root": {
            marginBottom: 8,
            width: 20,
            height: 20,
          },
        },
        label: {
          fontSize: "0.875rem",
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        rounded: {
          borderRadius: 12,
        },
      },
      variants: [
        {
          props: { variant: "logo" },
          style: {
            borderRadius: 8,
            width: 55,
            height: 55,
            marginRight: theme.spacing(2.25),
            "& .MuiAvatar-img": {
              objectFit: "contain",
            },
          },
        },
        {
          props: { color: "primary" },
          style: {
            background: theme.palette.primary.main,
            borderRadius: 5,
            "& .MuiSvgIcon-root": {
              width: 20,
            },
          },
        },
      ],
    },
    MuiListItemText: {
      styleOverrides: {
        root: {
          whiteSpace: "pre",
          overflow: "hidden",
          textOverflow: "ellipsis",
        },
      },
      variants: [
        {
          props: { variant: "medium" },
          style: {
            "& .MuiListItemText-primary": {
              fontSize: theme.typography.body2.fontSize,
            },
            "& .MuiListItemText-secondary": {
              fontSize: theme.typography.caption.fontSize,
            },
          },
        },
        {
          props: { color: "primary" },
          style: {
            "& .MuiListItemText-primary": {
              color: theme.palette.primary.main,
            },
          },
        },
      ],
    },
    MuiTypography: {
      styleOverrides: {
        gutterBottom: {
          marginBottom: theme.spacing(2),
        },
      },
      variants: [
        {
          props: { variant: "subtitle1" },
          style: {
            lineHeight: 1.4,
          },
        },
      ],
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          "&:last-child": {
            paddingBottom: theme.spacing(2),
          },
        },
      },
    },
    MuiIconButton: {
      variants: [
        {
          props: { variant: "contained" },
          style: {
            borderRadius: 5,
          },
        },
        {
          props: { color: "primary" },
          style: {
            background: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            "&:hover": {
              background: theme.palette.primary.dark,
            },
            "& .MuiSvgIcon-root": {
              "& path": { fill: theme.palette.primary.contrastText },
            },
          },
        },
      ],
    },
    AppSidebar: {
      styleOverrides: {
        root: {
          backgroundColor: theme.palette.grey[50],
          width: "100%",
          [theme.breakpoints.up("sm")]: {
            maxWidth: "428px",
          },
        },
        body: {
          padding: theme.spacing(3, 0, 1.25),
          flexGrow: 1,
        },
        closeButton: {
          position: "absolute",
          right: "32px",
          top: "24px",
          zIndex: 1,
          "& .MuiSvgIcon-root": {
            width: "14px",
            height: "14px",
            fontSize: "14px",
            fill: theme.palette.text.primary,
          },
        },
        navList: {},
        navDivider: {
          borderBottomWidth: "1px",
          margin: theme.spacing(0, 3),
          "&+ .MuiDivider-root": {
            display: "none",
          },
        },
        navListItem: {
          padding: theme.spacing(1.375, 4),
          "& .MuiTypography-root": {
            fontWeight: 300,
          },
          "& .MuiListItemIcon-root": {
            color: "inherit",
            minWidth: 40,
          },
          "& .MuiSvgIcon-root": {
            width: "18px",
            height: "18px",
          },
        },
        subList: {
          "& .MuiListItemIcon-root": {
            color: "inherit",
            minWidth: "30px",
          },
        },
        subDivider: {
          marginLeft: theme.spacing(8),
        },
        subListItem: {
          paddingLeft: "60px",
          "& .MuiTypography-root": {
            fontWeight: 500,
            fontSize: "0.875rem",
          },
          "& .MuiSvgIcon-root.MuiSvgIcon-fontSizeSmall": {
            fontSize: "0.5rem",
          },
          "& .MuiListItemIcon-root": {
            minWidth: "30px",
          },
          "& .MuiSvgIcon-root": {
            fontSize: "1rem",
          },
        },
      },
    },
    AppMobileNavigation: {
      styleOverrides: {
        root: {
          position: "fixed",
          zIndex: theme.zIndex.appBar,
          bottom: 0,
          left: 0,
          right: 0,
          display: "flex",
          justifyContent: "center",
        },
        menuButton: {
          textAlign: "center",
        },
      },
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        root: {
          gap: theme.spacing(1),
          paddingRight: theme.spacing(2.5),
          alignItems: "flex-start",
        },
        grouped: {
          "&:not(:last-of-type)": {
            borderRadius: (theme.shape.borderRadius * 2) / 3,
          },
          "&:not(:first-of-type)": {
            marginLeft: 1,
            borderRadius: (theme.shape.borderRadius * 2) / 3,
          },
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          borderRadius: (theme.shape.borderRadius * 2) / 3,
          color: theme.palette.primary.main,
          boxShadow: "rgb(0 0 0 / 20%) 0px 8px 24px",
          border: "none",
          "&.Mui-selected, &.Mui-selected:hover": {
            color: theme.palette.primary.contrastText,
            backgroundColor: theme.palette.primary.main,
          },
        },
      },
    },
    CategoryButton: {
      styleOverrides: {
        root: {
          padding: theme.spacing(1.5),
          width: 80,
          height: 80,
          flexDirection: "column",
          justifyContent: "center",
          color: theme.palette.text.primary,
          border: "none",
          boxShadow: theme.shadows[2],
          "& .MuiSvgIcon-root": {
            fontSize: "2.275rem",
          },
          "& .MuiTypography-root": {
            fontSize: "0.625rem",
            textAlign: "center",
            lineHeight: 1.1,
          },

          "&.Mui-selected": {
            "& .MuiBox-root": {
              color: theme.palette.primary.main,
            },
            "& .MuiTypography-root": {
              color: theme.palette.primary.main,
            },
          },
          "&.Mui-selected, &.Mui-selected:hover": {
            backgroundColor: "unset",
          },
        },
      },
    },
    AppCategoryFilterButton: {
      styleOverrides: {
        root: {
          padding: theme.spacing(1),
          width: 80,
          height: 80,
          flexDirection: "column",
          justifyContent: "start",
          gap: 0.25,
          border: "none",
          "& .MuiTypography-root": {
            fontSize: "0.625rem",
            textAlign: "center",
            lineHeight: 1.1,
            color: theme.palette.text.primary,
            fontWeight: theme.typography.fontWeightMedium,
          },
          "& .MuiBox-root": {
            backgroundColor: theme.palette.primary.contrastText,
            color: theme.palette.text.primary,
            borderRadius: `${(theme.shape.borderRadius * 2) / 3}px`,
            display: "flex",
            justifyContent: "center",
            "& .MuiSvgIcon-root": {
              width: 36,
              height: 36,
            },
          },
          "&.Mui-selected": {
            "& .MuiTypography-root, .MuiSvgIcon-root": {
              color: theme.palette.primary.main,
            },
          },
          "&.Mui-selected, &.Mui-selected:hover": {
            backgroundColor: "unset",
          },
        },
      },
    },
    AppCategoryFilterChip: {
      styleOverrides: {
        root: {
          padding: theme.spacing(0.5),
          borderRadius: "16px",
          boxShadow: "none",
          background: theme.palette.common.white,
          border: `1px solid ${theme.palette.grey[200]}`,
          whiteSpace: "nowrap",
          height: "32px",
          color: theme.palette.text.primary,
          fontWeight: 400,
          "&.MuiToggleButtonGroup-grouped:not(:last-of-type)": {
            borderRadius: "16px",
            margin: 0,
          },
          "&.MuiToggleButtonGroup-grouped:not(:first-of-type)": {
            border: `1px solid ${theme.palette.grey[200]}`,
            margin: 0,
            borderRadius: "16px",
          },
          "&.MuiToggleButtonGroup-grouped:first-of-type": {
            marginLeft: theme.spacing(3),
          },
          "&.Mui-selected": {
            "& svg": {
              fill: theme.palette.primary.contrastText,
            },
          },
          ":hover": {
            background: theme.palette.common.white,
          },
        },
        label: {
          fontSize: "0.8125rem",
          padding: theme.spacing(0, 1.5),
          letterSpacing: "0.00938em",
        },
        icon: {
          "& svg": {
            width: 16,
            height: 16,
            fill: theme.palette.primary.main,
            marginLeft: theme.spacing(0.5),
            marginRight: "-6px",
          },
        },
      },
    },
    WelcomePanel: {
      defaultProps: {
        showNavigation: false,
        showAmenity: true,
      },
      styleOverrides: {
        root: {
          backgroundImage: theme.palette.primary.main,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-end",
          padding: theme.spacing(6.75, 3, 2.5),
        },
        content: {
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          [theme.breakpoints.up("sm")]: {
            width: 460,
          },
        },
        logoContainer: {
          display: "flex",
          justifyContent: "flex-start",
          marginBottom: theme.spacing(3),
          "& img": {
            maxWidth: 130,
          },
        },
        searchBarContainer: {
          marginBottom: theme.spacing(3),
          width: "100%",
        },
        navItem: {
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-start",
          color: "white",
          flexBasis: 0,
          fontSize: theme.typography.subtitle1.fontSize,
          fontWeight: theme.typography.fontWeightRegular,
          lineHeight: "19px",
          flexGrow: "1",
          textAlign: "center",
          "& .MuiButton-startIcon": {
            margin: theme.spacing(0, 0, 1.5),
            width: 20,
            height: 20,
          },
        },
      },
    },
    AppHeader: {
      styleOverrides: {
        root: {
          position: "relative",
          boxShadow: "none",
          width: "100%",
          marginBottom: theme.spacing(3.75),
          [theme.breakpoints.up("sm")]: {
            marginBottom: theme.spacing(7.5),
          },
          [theme.breakpoints.up("md")]: {
            marginBottom: theme.spacing(10),
          },
          ":before": {
            content: "''",
            position: "absolute",
            zIndex: 1,
            height: 90,
            top: 0,
            left: 0,
            right: 0,
            backgroundSize: "100% 100px",
            backgroundPosition: "top 50px center",
            backgroundRepeat: "no-repeat",
            [theme.breakpoints.up("sm")]: {
              backgroundSize: "100% 140px",
              height: 100,
            },
            [theme.breakpoints.up("md")]: {
              backgroundSize: "100% 180px",
              height: 120,
            },
          },
        },
        toolbar: {
          zIndex: 2,
          minHeight: 70,
          justifyContent: "space-between",
          padding: theme.spacing(0, 3.25),
          width: `100%`,
          [theme.breakpoints.up("md")]: {
            margin: `0 auto`,
            maxWidth: 900,
          },
        },
        logoContainer: {
          display: "none",
        },
        titleContainer: {
          width: "100%",
          textAlign: "center",
        },
        clockContainer: {
          display: "none",
        },
        backButtonContainer: {
          display: "none",
        },
      },
    },
    AppPageLayout: {
      styleOverrides: {
        root: {
          display: "flex",
          flexDirection: "column",
          position: "relative",
          paddingBottom: `${(mixins.bottomNavigation?.height || 75) + 30}px`,
          minHeight: "calc(100% - 75px)",
          backgroundColor: theme.palette.background.default,
        },
        container: { padding: theme.spacing(3) },
      },
    },
    AppClock: {
      styleOverrides: {
        root: {
          color: theme.palette.text.primary,
          textTransform: "uppercase",
          whiteSpace: "pre-wrap",
          lineHeight: 1.2,
        },
        timeWrapper: {
          textAlign: "left",
          fontSize: "50px",
        },
        dateText: {
          textAlign: "right",
          fontSize: "30px",
        },
        timeLabel: {
          fontWeight: 300,
          fontSize: "16px",
        },
        timeText: {},
        timeUnitText: {
          fontWeight: 300,
          fontSize: "16px",
          marginBottom: theme.spacing(2),
        },
      },
    },
    AppMap: {
      styleOverrides: {
        quickLinkContainer: {
          overflowX: "scroll",
          padding: 0,
          display: "flex",
          //hide scrollbar
          "::-webkit-scrollbar": {
            display: "none",
          },
          MsOverflowStyle: "none",
          scrollbarWidth: "none",
        },
        searchBarContainer: {
          padding: theme.spacing(3, 3, 1, 3),
          [theme.breakpoints.up("md")]: {
            maxWidth: "350px",
          },
        },
        indoorMapContainer: {
          position: "absolute",
          zIndex: 0,
          width: "100%",
          height: "100%",
          "& > #map": {
            width: "100%",
            height: "100%",
          },
          "& > * > .maptalks-wrapper": {
            transition: "all 500ms ease-out",
            // * Fixing Map transition not working when navigate from Directory to Place Detail.
            WebkitTransition: "-webkit-transform 500ms ease-out",
          },
          "&.bottom-half": {
            overflow: "hidden",
            "& .maptalks-wrapper": {
              transform: `translate(0, 24%)`,
            },
          },
          "&.hidden": {
            opacity: 0,
            visibility: "hidden",
          },
        },
        venueSelector: {
          position: "fixed",
          bottom: "125px",
          left: "24px",
        },
        ordinalSelectMenu: {
          position: "fixed",
          bottom: "125px",
          right: "24px",
          display: "none",
        },
      },
    },
    AppMapLayout: {
      styleOverrides: {
        mapContainer: {
          width: "100%",
          position: "relative",
        },
        outletWrapper: {
          position: "relative",
        },
        headerWrapper: {
          display: "none",
        },
        footerWrapper: {
          display: "none",
        },
      },
    },
    BackIconButton: {
      styleOverrides: {
        root: { mr: 2 },
        icon: {
          width: 20,
          height: 20,
        },
      },
    },
    AppLoveEat: {
      styleOverrides: {
        levelStack: {
          gap: theme.spacing(4),
        },
        diningImageCard: {
          display: "block",
          "& .MuiCardHeader-root": {
            paddingBottom: theme.spacing(1),
          },
          "& .MuiCardHeader-root .MuiCardHeader-title": {
            lineHeight: 1.3,
          },
          "& .MuiCardHeader-content": {
            overflow: "hidden",
          },
          "& .MuiCardHeader-title": {
            overflow: "hidden",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            marginBottom: theme.spacing(0.1875),
          },
          "& .MuiCardHeader-avatar .MuiAvatar-root": {
            width: "50px",
            height: "50px",
          },
        },
        diningAvatar: {},
        diningAvatarFallBack: {},
      },
    },
    AppDirectionOverview: {
      defaultProps: {
        drawer: { drawerBleeding: config("device") === "mobile" ? 190 : 0 },
      },
      styleOverrides: {
        container: {
          display: "none",
        },
        wrapper: {
          padding: theme.spacing(4),
          "button, a": { pointerEvents: "all" },
          display: "flex",
          flexDirection: "column",
          gap: theme.spacing(1.5),
          borderRadius: `10px`,
        },
        bleedWrapper: {
          padding: theme.spacing(6.5, 3, 4),
          "button, a": { pointerEvents: "all" },
          display: "flex",
          flexDirection: "column",
          gap: theme.spacing(2.5),
        },
        primaryText: {
          marginBottom: theme.spacing(1.5),
        },
        secondaryText: {
          fontWeight: 700,
        },
        stepper: {
          "& .MuiStep-vertical": {
            ":not(:last-of-type)": {
              "& .MuiStepLabel-labelContainer": {
                borderBottom: `1px solid ${theme.palette.divider}`,
              },
            },
            "& .MuiStepLabel-labelContainer": {
              padding: theme.spacing(2.5, 0),
            },
          },
        },
        directionSummaryContainer: {
          [theme.breakpoints.down("sm")]: {
            height: "70px",
          },
        },
        stepOverviewContainer: {
          paddingLeft: theme.spacing(4),
          paddingRight: theme.spacing(4),
          "button, a": { pointerEvents: "all" },
        },
      },
    },
    AppTopBar: {
      styleOverrides: {
        root: {
          padding: theme.spacing(4, 2),
          top: 0,
          position: "absolute",
          width: "fit-content",
          left: 0,
          right: 0,
          zIndex: theme.zIndex.modal,
        },
        ordinalSelector: {
          position: "static",
          "&.MuiList-root": {
            borderRadius: 9999,
            width: "48px",
            height: "48px",
            padding: theme.spacing(0.4),
          },
          "& .MuiListItemButton-root": {
            height: "100%",
          },
        },
        backButton: {
          color: theme.palette.primary.main,
        },
      },
    },
    DirectionSearchComponent: {
      styleOverrides: {
        directionSearchWrapper: {
          boxShadow: theme.shadows[1],
          background: theme.palette.common.white,
          padding: theme.spacing(3, 2),
          borderRadius: "10px",
          display: "flex",
        },
        backIconWrapper: {
          display: "flex",
          alignItems: "start",
          justifyContent: "center",
          marginRight: theme.spacing(1),
        },
        directionWrapper: {
          flexGrow: 1,
          display: "flex",
        },
        switchIconWrapper: {
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          marginLeft: theme.spacing(1),
        },
        directionInputWrapper: {
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
        },
        directionIconWrapper: {
          display: "flex",
          justifyContent: "space-evenly",
          flexDirection: "column",
          marginRight: theme.spacing(1.5),
        },
        directionInput: {
          fontSize: "0.875rem",
          fontWeight: 500,
          flexGrow: 1,
          padding: theme.spacing(0, 0.5),
          "& .MuiInputBase-input": {
            padding: 0,
          },
        },
        iconButton: {
          color: theme.palette.text.primary,
          margin: "-5px",
          fontSize: "24px",
        },
        divider: {
          margin: theme.spacing(1, 0),
          borderBottomWidth: "1px",
        },
        clearIconButton: {
          color: theme.palette.text.primary,
          fontSize: "24px",
          margin: "-5px",
          "& .MuiSvgIcon-root": {
            height: "8px",
            width: "8px",
          },
        },
      },
    },
    AppDirectionSearch: {
      styleOverrides: {
        headerWrapper: {
          padding: theme.spacing(3),
          [theme.breakpoints.up("sm")]: {
            maxWidth: mixins.mobileDirectionSearch.width,
          },
        },
        paper: {
          borderRadius: "10px",
          padding: theme.spacing(0.75, 1.25),
          display: "flex",
          alignItems: "center",
        },
        formControlLabel: {
          margin: 0,
          "& .MuiFormControlLabel-label": {
            fontWeight: 500,
            marginLeft: theme.spacing(2),
          },
        },
      },
    },
    DirectionStep: {
      styleOverrides: {
        originIcon: {
          fill: theme.palette.primary.main,
          width: 37,
          height: 37,
        },
        destinationIcon: {
          fill: theme.palette.error.main,
          width: 28,
          height: 47,
        },
        highlightedText: {
          color: theme.palette.primary.main,
          textAlign: "inherit",
          fontWeight: "inherit",
          lineHeight: "inherit",
          fontSize: "inherit",
        },
      },
    },
    AppNavigationStep: {
      styleOverrides: {
        wrapper: {
          display: "flex",
          flexDirection: "column",
          gap: theme.spacing(2),
        },
        container: {
          position: "fixed",
          bottom: "25px",
          zIndex: theme.zIndex.modal,
          display: "flex",
          right: 0,
          left: 0,
          background: theme.palette.background.paper,
          borderRadius: `10px`,
          minHeight: 100,
          padding: theme.spacing(2, 3),
          flexDirection: "column",
          height: 170,
          marginLeft: theme.spacing(2),
          marginRight: theme.spacing(2),
          [theme.breakpoints.up("sm")]: {
            maxWidth: 565,
            margin: "auto",
          },
        },
        kioskBackButton: {
          display: "none",
        },
        title: {
          fontSize: "inherit",
          fontWeight: "bold",
        },
        primaryText: {
          color: theme.palette.text.primary,
          textAlign: "center",
          fontWeight: 600,
          lineHeight: 1.5,
        },
        highlightedText: {
          color: theme.palette.primary.main,
          textAlign: "inherit",
          fontWeight: "inherit",
          lineHeight: "inherit",
          fontSize: "inherit",
        },
        secondaryText: {
          textAlign: "center",
          color: theme.palette.grey[500],
          lineHeight: 1.5,
          fontWeight: 600,
          display: "-webkit-box",
          WebkitBoxOrient: "vertical",
          WebkitLineClamp: 2,
          overflow: "hidden",
          maxWidth: "100%",
        },
        locationText: {
          display: "none",
          color: theme.palette.text.primary,
          fontSize: 24,
          lineHeight: "33px",
          fontWeight: 700,
          textTransform: "uppercase",
          minWidth: 200,
        },
        stepTextContainer: {
          height: "40px",
          display: "flex",
          alignItems: "center",
        },
        originIcon: {
          display: "none",
          fontSize: "3.5rem",
        },
        destinationIcon: {
          display: "none",
          fontSize: "3.5rem",
          fill: theme.palette.primary.main,
        },
        headerWrapper: {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          color: theme.palette.common.black,
          fontSize: theme.typography.caption.fontSize,
          "& .MuiIconButton-root": {
            fontSize: "inherit",
          },
        },
        overviewWrapper: {
          justifyContent: "center",
          flexDirection: "row",
          gap: theme.spacing(1),
        },
        navButtonWrapper: {
          display: "none",
          flexDirection: "row",
          justifyContent: "center",
          gap: theme.spacing(4),
        },
        iconNavButton: {
          display: "block",
          padding: 0,
        },
        exitButtonWrapper: {
          position: "absolute",
          top: 0,
          right: 0,
          marginTop: theme.spacing(-6.5),
        },
        exitButton: {
          backgroundColor: theme.palette.background.default,
          border: `1px solid #ececec`,
          color: theme.palette.text.primary,
          fontWeight: 600,
          "& .MuiSvgIcon-root": {
            fill: theme.palette.primary.main,
          },
          "&:hover": {
            backgroundColor: theme.palette.grey[200],
          },
        },
      },
    },
    AppFeatureDetail: {
      styleOverrides: {
        root: {
          position: "relative",
          overflow: "initial",
          boxShadow: "none",
        },
        section: {
          margin: theme.spacing(3, 0),
          "&:first-of-type": {
            marginTop: 0,
          },
          color: theme.palette.text.secondary,
        },
        galleryImage: {
          objectFit: "cover",
          height: 130,
          width: 182,
          display: "block",
          boxShadow: theme.shadows[2],
          borderRadius: "6px",
          cursor: "pointer",
        },
        divider: {
          borderBottom: `8px solid #F7F7FF`,
          marginLeft: theme.spacing(-3),
          marginRight: theme.spacing(-3),
        },
        descriptionText: {
          color: theme.palette.text.secondary,
        },
        contactIcon: {
          color: theme.palette.primary.main,
          width: 24,
        },
        sectionTitle: {
          fontSize: theme.typography.subtitle1.fontSize,
          fontWeight: theme.typography.fontWeightBold,
          marginBottom: theme.spacing(2),
          color: theme.palette.text.primary,
        },
      },
    },
    AppImageModal: {
      styleOverrides: {
        root: {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: theme.spacing(2),
        },
        imageWrapper: {
          position: "relative",
          outline: "none",
          "& img": {
            borderRadius: "8px",
            width: "100%",
          },
          [theme.breakpoints.up("sm")]: {
            width: "400px",
          },
        },
        closeButton: {
          color: theme.palette.common.white,
          position: "absolute",
          top: theme.spacing(-4.5),
          right: theme.spacing(-1),
          [theme.breakpoints.up("sm")]: {
            right: theme.spacing(-2),
          },
        },
      },
    },
    HomePage: {
      styleOverrides: {
        root: {},
        contentContainer: {
          display: "flex",
          flexDirection: "column",
        },
        headerWrapper: {
          display: "none",
        },
        footerWrapper: {
          display: "none",
        },
        welcomePanelWrapper: {},
        featureCarouselWrapper: {
          padding: theme.spacing(3),
        },
        carouselWrapper: {
          display: "none",
        },
        welcomeContainer: {
          display: "none",
        },
        greetingText: {
          marginBottom: theme.spacing(3.125),
        },
        localeListContainer: {},
        localeButton: {},
        actionContainer: {
          marginTop: theme.spacing(8),
        },
        actionButton: {},
        textCard: {
          borderRadius: "15px",
          height: 136,
          display: "flex",
          alignItems: "center",
          p: theme.spacing(1),
          justifyContent: "center",
          flex: 1,
        },
        linkCardContainer: {
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 1.25,
          "&>*": {
            flex: 1,
          },
        },
        defaultCardIcon: {
          color: theme.palette.primary.main,
          display: "flex",
          justifyContent: "center",
        },
        defaultCardPrimaryText: {
          textAlign: "center",
          fontSize: "14px",
          lineHeight: 1.4,
        },
        defaultCardSecondaryText: {
          color: theme.palette.text.secondary,
          textAlign: "center",
          fontSize: "12px",
          lineHeight: 1.4,
        },
      },
    },
    FeatureActionContainer: {
      styleOverrides: {
        shortInfoActionWrapper: {
          "button, a": {
            pointerEvents: "all",
          },
          [theme.breakpoints.up("sm")]: {
            paddingBottom: theme.spacing(3),
          },
        },
        navigationButton: {
          padding: theme.spacing(1, 2),
        },
        actionButton: {
          borderColor: theme.palette.grey[600],
          color: theme.palette.grey[600],
          padding: theme.spacing(1, 2),
          "& .MuiSvgIcon-root": {
            color: theme.palette.primary.main,
          },
        },
      },
    },
    AppDesktopFeatureDialog: {
      styleOverrides: {
        dialog: {
          height: "100%",
          maxWidth: "50vw",
          left: "unset",
          "& .MuiDialog-container": {
            justifyContent: "flex-end",
          },
        },
        contentContainer: {
          padding: theme.spacing(3),
          background: theme.palette.common.white,
          boxShadow: "none",
          [theme.breakpoints.down("sm")]: {
            paddingTop: theme.spacing(1.25),
          },
        },
      },
    },
    EventPage: {
      styleOverrides: {
        cardListContainer: {
          display: "flex",
          flexDirection: "column",
        },
        currentEventsHeading: {
          margin: theme.spacing(0.25, 0, 3, 0),
        },
        upcomingEventsHeading: {
          margin: theme.spacing(5, 0, 3, 0),
        },
        emptyDataWrapper: {
          paddingTop: theme.spacing(2),
          paddingBottom: theme.spacing(10),
        },
        carouselContainer: {
          display: "none",
        },
        carousel: {
          "& .splide__pagination__page": {
            width: "14px",
            height: "14px",
          },
          "& .splide__pagination__page.is-active": {
            backgroundColor: theme.palette.primary.main,
            transition: `width ${theme.transitions.duration.short}ms ${theme.transitions.easing.easeInOut}, borderRadius ${theme.transitions.duration.standard}s ${theme.transitions.easing.easeInOut}`,
            transform: "none",
            width: 45,
            borderRadius: 30,
          },
          "& .splide__track": {
            margin: "auto",
          },
        },
        imageItem: {
          height: "auto",
          minHeight: "754px",
          width: "100%",
          borderRadius: "14px",
        },
      },
    },
    DirectoryPage: {
      styleOverrides: {
        root: {
          margin: theme.spacing(0, -3),
        },
        filterSection: {
          position: "sticky",
          display: "flex",
          flexDirection: "column",
          top: 0,
          zIndex: 1,
          marginTop: theme.spacing(-3),
          padding: 0,
          paddingTop: theme.spacing(2),
          background: theme.palette.background.paper,
        },
        categoryFilterBar: {
          gap: theme.spacing(2),
          padding: 0,
          paddingBottom: theme.spacing(2),
          "& .MuiToggleButton-root:first-of-type": {
            marginLeft: theme.spacing(1.5),
          },
          "& .MuiToggleButton-root:last-of-type": {
            marginRight: theme.spacing(1.5),
          },
        },
        scrollPane: {
          "& .MuiToggleButtonGroup-root": {
            gap: theme.spacing(1),
          },
          "& .MuiToggleButton-root:first-of-type": {
            marginLeft: theme.spacing(1.5),
          },
          "& .MuiToggleButton-root:last-of-type": {
            marginRight: theme.spacing(1.5),
          },
          "& .MuiDivider-root": {
            borderBottomWidth: 8,
          },
        },
        buildingWithLevelFilterSection: {
          display: "none",
        },
        divider: {
          display: "none",
        },
        list: {
          padding: theme.spacing(0, 1),
          paddingTop: theme.spacing(1),
        },
        levelFilterSelect: {},
        gridContainer: {
          display: "none",
        },
        gridItem: {
          width: "210px",
        },
      },
    },
    LevelFilterBar: {
      styleOverrides: {
        root: {
          paddingBottom: theme.spacing(3),
          "& .MuiToggleButton-root": {
            padding: theme.spacing(1, 3),
            flexShrink: 0,
          },
        },
      },
    },
    DirectionCardActions: {
      styleOverrides: {
        root: {
          height: "auto",
          padding: 0,
          display: "flex",
          flexDirection: "row",
          gap: theme.spacing(1),
          actionButton: {
            [theme.breakpoints.down("sm")]: {
              fontWeight: 700,
              "&.MuiButton-outlined": {
                border: `1px solid ${theme.palette.grey[400]}`,
              },
            },
          },
        },
      },
    },
    VerticalEventCard: {
      styleOverrides: {
        root: {
          minHeight: 395,
          display: "flex",
          flexDirection: "column",
          borderRadius: "10px",
        },
        cardMedia: {
          width: "100%",
          height: 258,
          objectFit: "cover",
        },
        cardContent: {
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          padding: theme.spacing(1.5, 1),
          "&.MuiCardContent-root:last-child": {
            paddingBottom: theme.spacing(1),
          },
        },
        cardTitle: {
          fontWeight: 600,
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: "-webkit-box",
          WebkitLineClamp: 3,
          WebkitBoxOrient: "vertical",
        },
        iconContainer: {
          width: "auto",
          marginRight: theme.spacing(1.5),
          "& .MuiSvgIcon-root": {
            height: 12,
            width: 10,
            color: theme.palette.primary.main,
          },
        },
        locationInfoContainer: {
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          "& .MuiTypography-root": {
            fontSize: "0.75rem",
          },
        },
      },
    },
    PromotionCard: {
      styleOverrides: {
        root: {
          boxShadow: theme.shadows[1],
          borderRadius: "10px",
          padding: 0,
          display: "flex",
          flexDirection: "column",
          minHeight: 307,
          "&.MuiCardContent-dense": {
            "& .MuiCardContent-root ": { padding: theme.spacing(1.5, 1.25) },
          },
        },
        cardMedia: {
          width: "100%",
          height: 179,
          borderRadius: "none",
          objectFit: "cover",
        },
        cardContent: {
          height: 128,
          padding: theme.spacing(1.25, 3),
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          "&:last-child": {
            paddingBottom: theme.spacing(1.25),
          },
          [theme.breakpoints.up("sm")]: {
            padding: theme.spacing(2.5, 4.5),
          },
        },
        cardTitle: {
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          color: theme.palette.text.primary,
          fontSize: "0.875rem",
        },
        cardInfoContainer: {
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          color: theme.palette.grey[700],
          fontSize: theme.typography.subTitle.fontSize,
        },
        durationContainer: {
          color: theme.palette.primary.contrastText,
          fontSize: theme.typography.subTitle.fontSize,
          backgroundColor: theme.palette.primary.main,
          borderRadius: "80px",
          padding: theme.spacing(1, 1),
          width: "fit-content",
        },
        iconContainer: {
          display: "flex",
          alignItems: "center",
          width: "auto",
          marginRight: theme.spacing(0.5),
          "& .MuiSvgIcon-root": {
            height: 14,
            color: theme.palette.primary.main,
            width: "auto",
          },
        },
      },
    },
    PromotionPage: {
      styleOverrides: {
        carouselContainer: {
          display: "none",
        },
        cardListContainer: {
          display: "flex",
          flexDirection: "column",
          gap: theme.spacing(2),
        },
        emptyDataContainer: {
          height: `calc(100vh - ${
            mixins.mobileHeader?.height + 30 //AppHeader marginBottom(30px)
          }px - ${
            mixins.bottomNavigation?.height + 30 // Prevent content page overlap with footer shadow by adding a bottom space (30px)
          }px)`,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        },
      },
    },
    HighlightFeatureCard: {
      styleOverrides: {
        root: {
          boxShadow: theme.shadows[1],
          borderRadius: "10px",
          display: "flex",
          flexDirection: "row",
          height: 136,
          padding: theme.spacing(1.5, 1.375),
        },
        cardMedia: {
          width: 74,
          maxHeight: 164,
          borderRadius: "5px",
          objectFit: "cover",
          [theme.breakpoints.up("sm")]: {
            width: 95,
          },
        },
        cardContent: {
          padding: theme.spacing(1.375, 0, 1.375, 3),
          "&:last-child": {
            paddingBottom: theme.spacing(1),
            [theme.breakpoints.up("sm")]: {
              padding: theme.spacing(1, 1, 1, 3),
            },
          },
        },
        contentWrapper: {
          height: "100%",
          justifyContent: "space-between",
        },
        cardTitle: {
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          color: theme.palette.text.primary,
        },
        eventInfoItem: {
          display: "flex",
          flexDirection: "row",
          color: theme.palette.text.secondary,
          "& .MuiTypography-root": {
            fontSize: "0.875rem",
            fontWeight: 500,
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: 1,
            WebkitBoxOrient: "vertical",
          },
        },
        iconWrapper: {
          width: "auto",
          marginRight: theme.spacing(1),
          "& .MuiSvgIcon-root": {
            height: 14,
            color: theme.palette.primary.main,
          },
        },
      },
    },
    ListPromotionItemCard: {
      styleOverrides: {
        root: {
          boxShadow: theme.shadows[1],
          borderRadius: `${(theme.shape.borderRadius * 2) / 3}px`,
          display: "flex",
          flexDirection: "row",
          height: 104,
          padding: theme.spacing(1, 0.5),
        },
        cardMedia: {
          width: 88,
          maxHeight: 88,
          borderRadius: "5px",
          objectFit: "cover",
          [theme.breakpoints.up("sm")]: {
            width: 95,
          },
        },
        cardContent: {
          padding: theme.spacing(0.5, 1, 0.5, 2),
          "&:last-child": {
            paddingBottom: theme.spacing(0.5),
            [theme.breakpoints.up("sm")]: {
              padding: theme.spacing(0.5, 1, 0.5, 2),
            },
          },
        },
        contentWrapper: {
          height: "100%",
          gap: theme.spacing(2),
          justifyContent: "space-between",
        },
        cardTitle: {
          fontWeight: 600,
          fontSize: "14px",
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          color: theme.palette.text.primary,
        },
        promotionInfoItem: {
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          color: theme.palette.text.secondary,
          "& .MuiTypography-root": {
            fontSize: "0.75rem",
            fontWeight: 400,
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: 1,
            WebkitBoxOrient: "vertical",
          },
        },
        iconWrapper: {
          width: "auto",
          marginRight: theme.spacing(1),
          "& .MuiSvgIcon-root": {
            height: 16,
            color: "inherit",
          },
        },
      },
    },
    ListPrivilegeItemCard: {
      styleOverrides: {
        root: {
          boxShadow: theme.shadows[1],
          borderRadius: "8px",
          padding: theme.spacing(1),
          display: "flex",
          alignItems: "center",
          gap: theme.spacing(2),
          "& .MuiSvgIcon-root": {
            color: theme.palette.grey[500],
            width: 16,
            height: 16,
          },
        },
        label: {
          color: theme.palette.text.secondary,
          fontWeight: 600,
        },
      },
    },
    FeatureSummary: {
      styleOverrides: {
        root: {
          height: "100%",
        },
        title: {
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
        },
        locationText: {
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: "-webkit-box",
          WebkitLineClamp: 1,
          WebkitBoxOrient: "vertical",
        },
        container: {
          display: "flex",
          gap: theme.spacing(2),
          boxShadow: "none",
        },
        logoContainer: {
          width: "64px",
          height: "64px",
        },
        logoImage: {
          borderRadius: "7.5px",
        },
        venueLogoContainer: {
          width: "100%",
          display: "flex",
          justifyContent: "center",
        },
        venueLogoImage: {
          height: "32px",
          width: "52px",
          objectFit: "contain",
        },
        infoSection: {},
        infoContainer: {},
        endAdornment: {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          width: "80px",
          height: "44px",
          paddingLeft: theme.spacing(2),
          "&:after": {
            content: "''",
            position: "absolute",
            height: "100%",
            width: "1px",
            left: 0,
            backgroundColor: theme.palette.grey[500],
            marginTop: theme.spacing(1.25),
            marginBottom: theme.spacing(1.25),
          },
        },
      },
    },
    ImageCard: {
      styleOverrides: {
        chipContainer: {
          position: "absolute",
          padding: "3%",
          right: 0,
          top: 0,
          zIndex: 1,
        },
        chip: {
          backgroundColor: theme.palette.error.main,
          borderRadius: "10px",
          color: theme.palette.error.contrastText,
        },
      },
    },
    FullScreenStart: {
      styleOverrides: {
        root: {
          cursor: "pointer",
          zIndex: -1,
          background: `linear-gradient(0deg, ${alpha(
            theme.palette.common.white,
            0.8
          )} 0%, ${alpha(theme.palette.common.white, 0)} 100%)`,
        },
        actionButtonWrapper: {
          position: "relative",
          display: "inline-block",
          "&:after, :before": {
            opacity: 0,
            display: "flex",
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            content: '""',
            height: "100%",
            width: "100%",
            border: `5px solid ${alpha(theme.palette.common.black, 0.4)}`,
            borderRadius: 9999,
            zIndex: -1,
          },
          "&:after": {
            animation: `ripple 3s infinite ${theme.transitions.easing.easeInOut}`,
          },
          "&:before": {
            animation: `ripple 3s infinite ${theme.transitions.easing.easeInOut} 0.5s`,
          },
        },
        actionButton: {
          position: "relative",
          fontSize: "1.5rem",
          textTransform: "uppercase",
          color: theme.palette.text.primary,
          borderRadius: 999,
          backgroundColor: theme.palette.common.white,
          transition: theme.transitions.create("all"),
          boxShadow: theme.shadows[10],
          paddingLeft: theme.spacing(2),
          paddingRight: theme.spacing(2),
          "&:hover": {
            boxShadow: theme.shadows[20],
            backgroundColor: theme.palette.common.white,
            paddingLeft: theme.spacing(4),
            paddingRight: theme.spacing(4),
          },
          [theme.breakpoints.up("md")]: {
            transform: "scale(1.2)",
          },
        },
      },
    },
    EmptyData: {
      styleOverrides: {
        root: {
          width: "100%",
          alignItems: "center",
          "& .MuiSvgIcon-root": {
            width: "80px",
            height: "80px",
            color: theme.palette.grey[200],
          },
        },
        secondaryText: {
          color: theme.palette.grey[600],
        },
        iconWrapper: {
          marginBottom: theme.spacing(1),
        },
      },
    },
    NewShopsPage: {
      styleOverrides: {
        emptyDataContainer: {
          height: `calc(100vh - ${mixins.mobileHeader?.height}px - ${
            mixins.bottomNavigation?.height + 30 //PageLayout paddingBottom
          }px)`,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        },
      },
    },
    OccupantCard: {
      styleOverrides: {
        root: {
          boxShadow: theme.shadows[1],
          borderRadius: "10px",
          width: "100%",
        },
        cardTitle: {
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          color: theme.palette.text.primary,
        },
        cardMediaWrapper: {
          position: "relative",
        },
        cardMedia: {
          height: "180px",
        },
        cardContent: {
          padding: theme.spacing(1.5, 2),
          "&:last-child": {
            paddingBottom: theme.spacing(1),
          },
          minHeight: "90px",
        },
      },
    },
    DirectionStepItem: {
      styleOverrides: {
        root: {
          "& .MuiStepLabel-iconContainer": {
            paddingRight: theme.spacing(2.5),
          },
          "&:first-of-type": {
            "& .MuiListItemText-primary": {
              fontWeight: 700,
            },
          },
          "&:last-of-type": {
            "& .MuiListItemText-primary": {
              fontWeight: 600,
            },
            "& .MuiStepLabel-iconContainer": {
              paddingLeft: theme.spacing(0.5),
              paddingRight: theme.spacing(3),
            },
          },

          "& .MuiStepIcon-root": {
            width: 37,
            height: 37,
            "& .MuiStepIcon-text": {
              fontSize: theme.typography.caption.fontSize,
            },
          },
          "& .MuiListItemText-primary": {
            width: "calc(100% - 24px - 32px)",
            whiteSpace: "normal",
            color: theme.palette.text.primary,
            fontSize: theme.typography.body2.fontSize,
            fontWeight: theme.typography.subtitle2.fontWeight,
          },
          "& .MuiListItemText-secondary": {
            fontSize: "0.75rem",
          },
          "& .MuiStepLabel-vertical": {
            padding: 0,
          },
          "& .MuiListItemSecondaryAction-root": {
            color: theme.palette.text.secondary,
            right: 0,
          },
        },
      },
    },
    FeatureListItem: {
      styleOverrides: {
        root: {
          display: "flex",
          flexDirection: "row",
          paddingLeft: theme.spacing(2),
          paddingRight: theme.spacing(2),
        },
        //adornmentDivider and adornmentWrapper can be remove if not necessary for tourist-friendly
        adornmentDivider: {
          marginRight: theme.spacing(1),
          borderRight: "1px solid rgba(203, 203, 203, 0.5)",
        },
        adornmentWrapper: {
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          flex: 1,
          "& img": {
            objectFit: "contain",
            maxWidth: "60px",
            maxHeight: "55px",
          },
        },
        bottomDivider: {
          borderBottom: "1px solid rgba(203, 203, 203, 0.5)",
          marginLeft: theme.spacing(2),
          marginRight: theme.spacing(2),
        },
        listItemContent: {
          display: "flex",
          width: "100%",
          gap: theme.spacing(2),
        },
        listItemText: {
          flex: 2.5,
          display: "flex",
          flexDirection: "column",
          gap: theme.spacing(0.5),
          "& .MuiListItemText-primary": {
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            lineHeight: 1.2,
            fontSize: "1rem",
            fontWeight: 700,
            whiteSpace: "normal",
          },
          "& .MuiListItemText-secondary": {
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: 1,
            WebkitBoxOrient: "vertical",
            lineHeight: 1.4,
            whiteSpace: "normal",
          },
        },
      },
    },
    UserLocationListItem: {
      styleOverrides: {
        icon: {
          background: "transparent",
          color: theme.palette.text.primary,
          fontSize: "3rem",
          "& .MuiSvgIcon-root": {
            fontSize: "inherit",
          },
        },
      },
    },
    DirectionSearchContainer: {
      styleOverrides: {
        wrapper: {
          width: "100%",
          [theme.breakpoints.down("sm")]: {
            maxWidth: "100%",
          },
        },
        suggestionList: {
          marginTop: theme.spacing(8),
          // direction search box = 108 px
          // avoid step button 32 px + top-bottom margin 16px * 2 = 64px
          // top-bottom page layout padding 24px * 2 = 48px
          maxHeight: `calc(100vh - 220px)`,
          overflow: "auto",
          background: theme.palette.background.paper,
          boxShadow: theme.shadows[1],
          position: "absolute",
          zIndex: theme.zIndex.drawer + 1,
          borderRadius: "10px",
          //left-right layout padding = 24 * 2 = 48px
          width: `calc(100vw - 48px)`,
          [theme.breakpoints.up("sm")]: {
            //left-right layout padding = 24 * 2 = 48px
            maxWidth: mixins.mobileDirectionSearch.width - 48,
          },
        },
      },
    },
    AppOrdinalSelector: {
      styleOverrides: {
        root: {
          position: "fixed",
          bottom: theme.spacing(27),
          left: theme.spacing(2),
          borderRadius: `${theme.shape.borderRadius * 1.33}px`,
          backgroundColor: theme.palette.background.default,
          boxShadow: theme.shadows[15],
          width: 40,
          padding: theme.spacing(0.25),
        },
        listItemButton: {
          justifyContent: "center",
          borderRadius: 9999,
          backgroundColor: theme.palette.background.default,
          fontSize: "0.875rem",
          fontWeight: theme.typography.fontWeightRegular,
          transition: `color ${theme.transitions.duration.short}ms ${theme.transitions.duration.short}ms ${theme.transitions.easing.easeInOut} , background-color ${theme.transitions.duration.short}ms ${theme.transitions.easing.easeInOut}`,
          paddingTop: theme.spacing(0.9375),
          paddingBottom: theme.spacing(0.9375),
          "&.Mui-selected": {
            color: theme.palette.primary.contrastText,
            backgroundColor: theme.palette.primary.main,
            "&:hover": {
              backgroundColor: theme.palette.primary.main,
            },
          },
        },
      },
    },
    VenueSelector: {
      styleOverrides: {
        buttonWrapper: {
          display: "flex",
          justifyContent: "center",
          margin: theme.spacing(0, 2),
        },
        button: {
          padding: 0,
          backgroundColor: theme.palette.primary.main,
          width: "47px",
          height: "47px",
          borderRadius: "50%",
          "&.Mui-selected": {
            backgroundColor: theme.palette.primary.main,
            ":hover": {
              backgroundColor: theme.palette.primary.main,
            },
          },
        },
        icon: {
          width: "100%",
          height: "100%",
        },
        currentVenueChip: {
          position: "absolute",
          height: "8px",
          width: "32px",
          bottom: "-4px",
        },
      },
    },
    OrdinalSelectMenu: {
      styleOverrides: {
        root: {
          backgroundColor: theme.palette.background.paper,
          height: "32px",
          width: "96px",
          textAlign: "center",
        },
        paper: {
          backgroundColor: "transparent",
          boxShadow: "none",
          alignItems: "center",
          maxHeight: 200,
          //hide scrollbar
          "::-webkit-scrollbar": {
            display: "none",
          },
          MsOverflowStyle: "none",
          scrollbarWidth: "none",
        },
        menuList: {
          display: "flex",
          alignItems: "center",
          flexDirection: "column",
        },
        menuItem: {
          margin: theme.spacing(1),
          backgroundColor: theme.palette.background.paper,
          borderRadius: "50%",
          height: "40px",
          width: "40px",
          padding: 0,
          minHeight: "40px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          transformOrigin: "top center",
          animation: `slideUp 300ms ease-in-out forwards`,
          "&.Mui-selected": {
            backgroundColor: theme.palette.primary.main,
            border: `1px solid ${theme.palette.primary.contrastText} `,
            color: theme.palette.primary.contrastText,
            ":hover": {
              backgroundColor: theme.palette.primary.main,
            },
          },
          ":hover": {
            backgroundColor: theme.palette.primary.main,
          },
        },
        text: {
          fontSize: "12px",
          color: theme.palette.text.secondary,
        },
      },
    },
    BorderLinearProgress: {
      styleOverrides: {
        root: {
          position: "relative",
          width: "100%",
          "&:after": {
            position: "absolute",
            top: "50%",
            transform: "translateY(-50%)",
            content: "''",
            display: "block",
            height: 4,
            width: "100%",
            borderRadius: 5,
            backgroundColor: theme.palette.grey[300],
            right: 0,
          },
        },
        linearProgress: {
          height: 20,
          borderRadius: 15,
          zIndex: 1,
          ":after": {
            position: "absolute",
            zIndex: 5,
            top: "50%",
            transform: "translateY(-50%)",
            height: 4,
            content: "''",
            display: "block",
          },
          "&.MuiLinearProgress-colorPrimary": {
            backgroundColor: "transparent",
            "&.Mui-start": {
              ":after": {
                backgroundColor: theme.palette.primary.main,
              },
            },
            ":after": {
              position: "absolute",
              top: "50%",
              transform: "translateY(-50%)",
              content: "''",
              display: "inline-block",
              width: 14,
              height: 14,
              borderRadius: "50%",
              backgroundColor: theme.palette.grey[300],
              zIndex: 1,
              left: 0,
            },
            "&.Mui-complete": {
              ":before": {
                backgroundColor: theme.palette.primary.main,
              },
              ":after": {
                backgroundColor: theme.palette.primary.main,
              },
            },
            ":before": {
              position: "absolute",
              top: "50%",
              transform: "translateY(-50%)",
              content: "''",
              display: "inline-block",
              width: 14,
              height: 14,
              borderRadius: "50%",
              backgroundColor: theme.palette.grey[300],
              zIndex: 1,
              right: 0,
            },
          },
          "& .MuiLinearProgress-bar": {
            margin: "auto 0",
            height: 4,
            borderRadius: 5,
            backgroundColor: theme.palette.primary.main,
            boxShadow: `0px 0px 10px ${theme.palette.primary.main}`,
          },
        },
      },
    },
  },
})
